import { ChevronRightIcon } from '@heroicons/react/16/solid';

import { Logo } from '@/components/Logo';
import { GridBackdrop } from '@/components/UI/GridBackdrop';
import { CBBIcon, CFBIcon, NFLIcon } from '@/pages/order/components/ProductIcons';
import { isIosNative } from '@/renderer/utils/isIosNative';
import { SafeTopOwner } from '@/renderer/utils/safeTop';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// All available products, so we can list every active sport + its lowest price.
// `/api/v1/product` returns products with services attached, so `sportIds` is
// populated (typed `Id[]` only under `<true>`; the default `<false>` narrows it
// to `never`). Mirror the canonical getProducts in pages/order/+Page.tsx.
const getProducts: RequestSchema<API.MembershipProduct<true>[]> = {
  name: 'appLandingProducts',
  input: '/api/v1/product',
};

// Release stats for the all-time record shown per sport on iOS. Fetched client-side
// (useRequest) so the records are pulled live at runtime and never bake stale into the
// prerendered bundle. Same query the marketing homepage + verified-record page use.
const getStats: RequestSchema<API.ReleaseStats> = {
  name: 'appLandingStats',
  input:
    '/api/v1/release/stats?page[limit]=100&data[playType][]=side&data[playType][]=total&data[playType][]=prop&data[playType][]=future&data[playType][]=not-available',
};

// Compact service rows — sport + route + a short line. Order controls display.
const SPORTS = [
  {
    sportId: 3,
    name: 'NFL',
    route: '/order/nfl',
    tagline: 'Sides, totals & halves',
    Icon: NFLIcon,
  },
  {
    sportId: 1,
    name: 'College Football',
    route: '/order/cfb',
    tagline: 'FBS & FCS sides & totals',
    Icon: CFBIcon,
  },
  {
    sportId: 2,
    name: 'College Basketball',
    route: '/order/cbb',
    tagline: 'Sides & totals through March',
    Icon: CBBIcon,
  },
];

function isOfferVisible(offer: API.MembershipProductOffer): boolean {
  return offer?.hidden !== true && (offer as any)?.available !== false && offer?.price != null;
}

/**
 * Simplified landing shown to logged-out users inside the native app, in place of
 * the full marketing page. A full-height welcome screen — brand + services up top,
 * stacked auth buttons pinned to the bottom. Web is unaffected; IndexRoute only
 * renders this on native.
 *
 * iOS shows no pricing and no purchase entry points (App Store reader-app rule,
 * #2029): the sport rows are informational, with the all-time record as the
 * subtitle. Android keeps its existing web-checkout rows.
 *
 * The backdrop is the same GridBackdrop the sign-in screen uses (#2039), so the
 * landing and the screen its primary button leads to read as one surface.
 */
export default function AppLanding() {
  const productsRequest = useRequest(getProducts);
  const products = productsRequest.data;

  const services = SPORTS.map((sport) => {
    const prices = (products ?? [])
      .filter((p) => p.sportIds?.includes(sport.sportId))
      .flatMap((p) => p.offers ?? [])
      .filter(isOfferVisible)
      .map((o) => o.price)
      .filter((p): p is number => typeof p === 'number' && p > 0);
    return { ...sport, price: prices.length ? Math.min(...prices) : null };
  }).filter((s) => s.price != null);

  const statsData = useRequest(getStats).data;

  const ios = isIosNative();

  // All-time record per sport, shown as each sport's subtitle on iOS. Pulled live at
  // runtime (both feeds come from useRequest, not the prerendered page data, so the
  // numbers never bake stale into the app bundle), and sourced to match what the rest
  // of the site publishes:
  //  - NFL / CFB: the curated `ui.resultSummaries` all-time value on the membership
  //    product (the same figure the product cards render, e.g. CFB "1239-980-21").
  //  - CBB: has no membership product (separate legacy `products` system, often out of
  //    season), so aggregate the live `/api/v1/release/stats` cumulative, which is the
  //    source the verified-record page uses for every sport.
  // Any sport with no record available renders its descriptive tagline instead, so a
  // row never shows an empty or 0-0 record.
  const formatRecord = (win: number, loss: number): string | null => {
    if (win + loss <= 0) {
      return null;
    }
    return `${win}-${loss} (${((win / (win + loss)) * 100).toFixed(1)}%)`;
  };

  // A curated resultSummaries value is "W-L-P" (e.g. "1239-980-21"); percentage is
  // win/(win+loss), pushes excluded, matching the site's published figures.
  const productRecord = (sportId: number): string | null => {
    const product = (products ?? []).find(
      (p) => p.sportIds?.includes(sportId) && (p as any).ui?.resultSummaries?.[0]?.value,
    );
    const value: string | undefined = (product as any)?.ui?.resultSummaries?.[0]?.value;
    const [win, loss] = (value ?? '').split('-').map((n) => Number.parseInt(n, 10));
    return Number.isFinite(win) && Number.isFinite(loss) ? formatRecord(win, loss) : null;
  };

  // Sum a sport's wins/losses across every season in the release-stats feed.
  const statsRecord = (sportId: number): string | null => {
    const seasons = (statsData as any)?.seasonId as Record<string, any> | undefined;
    if (!seasons) {
      return null;
    }
    let win = 0;
    let loss = 0;
    for (const key of Object.keys(seasons)) {
      const entry = seasons[key];
      const entrySportId = Number(
        entry?.meta?.season?.[key]?.sportId ?? entry?.meta?.sport?.[0]?.id,
      );
      if (entrySportId !== sportId) {
        continue;
      }
      win += entry?.stats?.win?.total ?? 0;
      loss += entry?.stats?.loss?.total ?? 0;
    }
    return formatRecord(win, loss);
  };

  const recordFor = (sportId: number): string | null =>
    // NFL (3) / CFB (1) use the curated product record; CBB (2) has none, so use the
    // live release-stats aggregate.
    sportId === 2 ? statsRecord(sportId) : productRecord(sportId);

  return (
    // This screen carries the iOS safe-area insets itself (the shell paints
    // edge-to-edge under them) so the backdrop runs all the way to the top of the
    // display instead of stopping at an opaque band; SafeTopOwner below tells the
    // layout to add none of its own (#2028). The insets are STACKED on top of the
    // padding (calc, not max) so the bottom copy clears the home indicator rather
    // than sitting in its band. env() is 0 on Android/web.
    //
    // The explicit text colour is not redundant: this renders inside the marketing
    // layout, whose default copy colour is white (its pages are dark-themed), so an
    // unstyled label here would be white-on-white in light mode.
    <div className="relative flex min-h-full flex-col bg-[Canvas] px-5 pt-[calc(2rem+var(--safe-top))] pb-[calc(1.5rem+var(--safe-bottom))] text-zinc-950 dark:text-white">
      <SafeTopOwner />
      <GridBackdrop />

      {ios ? (
        // Spare in-app landing (iOS): the priced services are hidden for reader-app
        // compliance, so center the brand + "sports we cover" in the space above the
        // pinned auth buttons. This keeps the screen balanced instead of jamming the
        // logo against the top inset with a large empty band below.
        <div className="relative flex flex-1 flex-col justify-center gap-10 py-6">
          {/* Brand + pitch */}
          <div className="text-center">
            <div className="flex justify-center">
              <Logo className="h-16 w-auto select-none" />
            </div>
            <h1 className="mt-6 font-bold text-3xl text-zinc-900 tracking-tight dark:text-white">
              Sharp picks since 1997.
            </h1>
            <p className="mx-auto mt-2 max-w-xs text-sm text-zinc-500 dark:text-zinc-400">
              56% ATS long-term, independently verified by betstamp.
            </p>
          </div>

          {/* Sports we cover — informational only: non-interactive rows, no price,
              no route/chevron (no pricing or purchase entry points on iOS). */}
          <div>
            <p className="mb-2 font-medium text-xs text-zinc-400 uppercase tracking-wide dark:text-zinc-500">
              Sports we cover
            </p>
            <div className="flex flex-col gap-2">
              {SPORTS.map((s) => (
                <div
                  key={s.sportId}
                  className="flex items-center gap-3 rounded-xl border border-zinc-950/10 px-4 py-3 dark:border-white/10"
                >
                  <s.Icon className="size-8 shrink-0 fill-emerald-600 dark:fill-emerald-400" />
                  <div className="min-w-0 flex-1 text-left">
                    <div className="font-semibold text-sm">{s.name}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {recordFor(s.sportId) ?? s.tagline}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative">
          {/* Brand + pitch */}
          <div className="text-center">
            <div className="flex justify-center">
              <Logo className="h-16 w-auto select-none" />
            </div>
            <h1 className="mt-6 font-bold text-3xl text-zinc-900 tracking-tight dark:text-white">
              Sharp picks since 1997.
            </h1>
            <p className="mx-auto mt-2 max-w-xs text-sm text-zinc-500 dark:text-zinc-400">
              56% ATS long-term, independently verified by betstamp.
            </p>
          </div>

          {/* Services — non-iOS only (reader app hides pricing/purchase on iOS) */}
          {services.length > 0 && (
            <div className="mt-8">
              <p className="mb-2 font-medium text-xs text-zinc-400 uppercase tracking-wide dark:text-zinc-500">
                Available services
              </p>
              <div className="flex flex-col gap-2">
                {services.map((s) => (
                  <a
                    key={s.sportId}
                    href={s.route}
                    className="flex items-center gap-3 rounded-xl border border-zinc-950/10 px-4 py-3 transition-colors hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5"
                  >
                    <s.Icon className="size-8 shrink-0 fill-emerald-600 dark:fill-emerald-400" />
                    <div className="min-w-0 flex-1 text-left">
                      <div className="font-semibold text-sm">{s.name}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.tagline}</div>
                    </div>
                    <div className="shrink-0 text-sm text-zinc-500 dark:text-zinc-400">
                      from ${s.price}
                    </div>
                    <ChevronRightIcon className="size-4 shrink-0 text-zinc-300 dark:text-zinc-600" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Auth — pinned to the bottom, stacked full-width. Most people installing
          the app already have an account, so Sign in is the primary action;
          Create a free account is the secondary option below it. The forest
          green matches the auth screens' primary button (authPrimaryButtonClass). */}
      <div className="relative mt-auto flex flex-col gap-2.5 pt-10">
        <a
          href="/signin"
          className="rounded-xl bg-[#15734A] py-3.5 text-center font-bold text-sm text-white shadow-sm transition hover:bg-[#125f3d] dark:bg-[#34B473] dark:text-zinc-950 dark:hover:bg-[#43c983]"
        >
          Sign in
        </a>
        <a
          href="/register"
          className="rounded-xl py-3.5 text-center font-semibold text-sm text-zinc-700 ring-1 ring-zinc-950/15 transition hover:bg-zinc-50 dark:text-zinc-200 dark:ring-white/15 dark:hover:bg-white/10"
        >
          Create a free account
        </a>
        <p className="text-center text-xs text-zinc-400 dark:text-zinc-500">
          Free account: get free plays and service updates.
        </p>
      </div>
    </div>
  );
}
